import React, { lazy, Suspense, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "@fontsource/poppins";
import "../src/App.css";
import axios from "axios";
import jsonData from "./components/Jsons/ai-branding.json";
import LoanOfficerBanner from "./components/LoanOfficerBanner/LoanOfficerBanner";
import Helmet from "react-helmet";

// Lazy load components
const ReelSection = lazy(() => import("./components/ReelSection/reel"));
const ForbsCarousel = lazy(() => import("./components/ForbsCarousel/index"));
const Flowchart = lazy(() => import("./components/Flowchart/FlowChart"));
const NewsHighlights = lazy(() => import("./components/Carousel/carousel"));
const Footer = lazy(() => import("./components/Footer/index"));
const Highlights = lazy(() => import("./components/Highlights/Highlights"));
const LogoSection = lazy(() => import("./components/Logo Section/Logo"));
const SignUpForm = lazy(() => import("./components/SignUpForm/SignUpForm"));
const VideoSection = lazy(() => import("./components/VideoSection/ExploreSection"));

// Spinner component with inline styles
const Spinner = () => {
  const spinnerStyle = {
    display: "flex",
    justifyContent: "center",
  };

  const circleStyle = {
    border: "8px solid #f3f3f3",
    borderTop: "8px solid #3498db",
    borderRadius: "50%",
    width: "50px",
    height: "50px",
    animation: "spin 1s linear infinite",
  };

  return (
    <div style={spinnerStyle}>
      <div style={circleStyle}></div>
    </div>
  );
};

function LoHome() {
  const { agentName } = useParams();
  const navigate = useNavigate();
  const agentPageName = `${agentName.toLowerCase()}.json`;

  const [loanOfficerData, setLoanOfficerData] = useState();
  const [bannerData, setBannerData] = useState();
  const [highlightsData, setHighlightsData] = useState();
  const [formData, setFormData] = useState();
  const [links, setLinks] = useState(null);
  const [workflowId, setWorkflowId] = useState();
  const [workflowKey, setWorkflowKey] = useState();
  const [showOtherComponents, setShowOtherComponents] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch agent data from Path 1 and Path 2
  useEffect(() => {
    const fetchAgentData = async () => {
      const path1 = axios.get(
        process.env.REACT_APP_CDN + "workshop/jsons/" + agentPageName
      );
      const path2 = axios.get(
        process.env.REACT_APP_CDN +
          "swmc/jsons/loanOfficer/loanOfficerJson/" +
          agentPageName
      );
  
      const results = await Promise.allSettled([path1, path2]);
  
      let agentData = null;
  
      // Handle Path 1 result
      if (results[0].status === "fulfilled") {
        agentData = results[0].value.data[0] || results[0].value.data || null;
  
        // If path1 data is empty, fallback to path2
        if (!agentData || Object.keys(agentData).length === 0) {
          console.log("Path 1 data is empty, trying Path 2...");
          agentData = null; // Reset agentData to fetch from path2
        }
      }
  
      // Handle Path 2 result if Path 1 is empty or not fulfilled
      if (!agentData && results[1].status === "fulfilled") {
        agentData = results[1].value.data;
        if (Array.isArray(agentData) && agentData.length > 0) {
          agentData = agentData[0];
        } else if (agentData?.data) {
          agentData = agentData.data;
        }
  
        // If path2 data is empty, fallback to path1
        if (!agentData || Object.keys(agentData).length === 0) {
          console.log("Path 2 data is empty, trying Path 1...");
          agentData = null; // Reset to null to trigger final fallback
        }
      }
  
      // If no valid data found from either path, navigate to the home page
      if (!agentData) {
        console.log("No valid data found, redirecting to home page.");
        navigate("/");
        return;
      }
  
      setLoanOfficerData(agentData);
      setLoading(false); // Mark loading as complete
    };
  
    fetchAgentData();
  }, [agentPageName, navigate]);
  

  // Fetch general links
  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_ANGELAI_JSON);
        setLinks(response.data);
      } catch (error) {
        setLinks(jsonData); // Use local JSON data if fetching fails
      }
    };
    fetchLinks();
  }, []);

  // Fetch workflow details
  useEffect(() => {
    const fetchWorkflowKey = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_CDN_WORKFLOW_KEY);
        const fetchedWorkflowId = response.data.Workshop;
        setWorkflowId(fetchedWorkflowId);

        const workflowKeyResponse = await axios.post(
          process.env.REACT_APP_CDN_WORKFLOW_API,
          { workflow: fetchedWorkflowId }
        );
        setWorkflowKey(workflowKeyResponse.data.key);
      } catch (error) {
        console.error("Error fetching workflow key:", error);
      }
    };
    fetchWorkflowKey();
  }, []);

  // Scroll-triggered component loading
  useEffect(() => {
    const handleScroll = () => {
      // Trigger scroll logic only for screen sizes below 992px
      if (window.innerWidth < 992) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.body.offsetHeight;
        const scrollPosition =
          window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
        const maxScroll = documentHeight - windowHeight;
        const percentage = (scrollPosition / maxScroll) * 100;
  
        if (percentage > 50) {
          setShowOtherComponents(true);
        }
      }
    };
  
    // Default state for larger screens
    if (window.innerWidth >= 992) {
      setShowOtherComponents(true);
    }
  
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  

  if (loading || !links || !workflowKey) {
    return <Spinner />; // Show Spinner while loading
  }

  return (
    <>
      <Helmet>
  <link rel="canonical" href={window.location.href.endsWith('/') ? window.location.href : `${window.location.href}/`} />
</Helmet>


      <div className="bodyContainer">
        <LoanOfficerBanner loanOfficerData={loanOfficerData} links={links} />

        

        {showOtherComponents && (
          <Suspense fallback={<Spinner />}>
            <>
            <ReelSection fetchpriority="low" />
        <Flowchart links={links.current} WorkflowKey={workflowKey} fetchpriority="low" />
              <ForbsCarousel fetchpriority="low" />
              {highlightsData && (
                <Highlights
                  highlightsData={highlightsData}
                  links={links.current}
                  WorkflowKey={workflowKey}
                  fetchpriority="low"
                />
              )}
            </>
            <>
              <LogoSection fetchpriority="low" />
              <NewsHighlights fetchpriority="low" />
            </>
            <>
              <SignUpForm loanOfficerData={loanOfficerData} fetchpriority="low" />
              <VideoSection links={links.current} fetchpriority="low" />
            </>
            <>
              <Footer links={links.current} fetchpriority="low" />
            </>
          </Suspense>
        )}
      </div>
    </>
  );
}

export default LoHome;
